import React, { useState } from 'react';
import { ChevronUp, ChevronDown, TrendingUp, TrendingDown } from 'lucide-react';

/**
 * ProgressionTable - Displays progression data in tabular format
 * Shows range, correct/total, accuracy, and trend
 */
const ProgressionTable = ({
  data = [],
  category = null,
  isLoading = false
}) => {
  const [sortBy, setSortBy] = useState('range'); // 'range' or 'accuracy'
  const [sortDesc, setSortDesc] = useState(false);

  const sortedData = React.useMemo(() => {
    if (!data || data.length === 0) return [];

    const sorted = [...data];
    sorted.sort((a, b) => {
      let compareValue = 0;

      if (sortBy === 'range') {
        // Extract numeric part of range for proper sorting
        const aNum = parseInt(a.range.split('-')[0].replace('Q', ''));
        const bNum = parseInt(b.range.split('-')[0].replace('Q', ''));
        compareValue = aNum - bNum;
      } else if (sortBy === 'accuracy') {
        compareValue = a.accuracy - b.accuracy;
      }

      return sortDesc ? -compareValue : compareValue;
    });

    return sorted;
  }, [data, sortBy, sortDesc]);

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'text-green-400 bg-green-400/10';
    if (accuracy >= 70) return 'text-yellow-400 bg-yellow-400/10';
    if (accuracy >= 60) return 'text-cyan-400 bg-cyan-400/10';
    return 'text-red-400 bg-red-400/10';
  };

  const getTrendIcon = (current, previous) => {
    if (!previous) return null;
    if (current > previous) {
      return <ChevronUp className="w-4 h-4 text-green-400" />;
    } else if (current < previous) {
      return <ChevronDown className="w-4 h-4 text-red-400" />;
    }
    return null;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDesc(!sortDesc);
    } else {
      setSortBy(field);
      setSortDesc(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 flex flex-col items-center justify-center h-64">
        <div className="text-gray-400 text-center">
          <p className="mb-2">No progression data available</p>
          <p className="text-sm text-gray-500">Answer questions to track your progress</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-cyan-500" />
        <h3 className="text-lg font-semibold text-white">
          {category ? `${category} - Progression Details` : 'Progression Details'}
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left py-3 px-4 font-semibold text-gray-300 cursor-pointer hover:text-white"
                  onClick={() => handleSort('range')}>
                <div className="flex items-center gap-2">
                  Range
                  {sortBy === 'range' && (
                    sortDesc ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                Correct / Total
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300 cursor-pointer hover:text-white"
                  onClick={() => handleSort('accuracy')}>
                <div className="flex items-center justify-center gap-2">
                  Accuracy
                  {sortBy === 'accuracy' && (
                    sortDesc ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
                  )}
                </div>
              </th>
              <th className="text-center py-3 px-4 font-semibold text-gray-300">
                Trend
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => {
              const previousItem = index > 0 ? sortedData[index - 1] : null;
              const accuracyClass = getAccuracyColor(item.accuracy);

              return (
                <tr key={`${item.range}-${index}`} className="border-b border-gray-800 hover:bg-gray-800/50 transition">
                  <td className="py-3 px-4 text-gray-300 font-medium">
                    {item.range}
                  </td>
                  <td className="py-3 px-4 text-center text-gray-300">
                    <span className="font-medium">{item.correct}</span>
                    <span className="text-gray-500">/{item.total}</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-3 py-1 rounded-full font-semibold ${accuracyClass}`}>
                      {item.accuracy}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {getTrendIcon(item.accuracy, previousItem?.accuracy)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary stats */}
      <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-4 gap-4">
        <div>
          <p className="text-xs text-gray-400 mb-1">Total Windows</p>
          <p className="text-2xl font-bold text-cyan-500">{sortedData.length}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Highest Accuracy</p>
          <p className="text-2xl font-bold text-green-400">
            {Math.max(...sortedData.map(d => d.accuracy))}%
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Lowest Accuracy</p>
          <p className="text-2xl font-bold text-red-400">
            {Math.min(...sortedData.map(d => d.accuracy))}%
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Total Questions</p>
          <p className="text-2xl font-bold text-white">
            {sortedData.reduce((sum, d) => sum + d.total, 0)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProgressionTable;
